import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import {
  VmCallPreviewRequest,
  VmPatient,
  VmSettings,
  VmTranscript,
  Voicemail,
} from '@models';
import { HttpService } from '../http.service';
import { VoicemailGateway, VoicemailGatewayError } from './voicemail.gateway';
import {
  toTranscript,
  toVmSettings,
  toVoicemail,
  VoicemailDto,
  VoicemailPatientDto,
  VoicemailSettingsDto,
  VoicemailTranscriptDto,
} from './voicemail.mapper';

/**
 * Provider-authorized voicemail gateway.
 *
 * The abstract boundary is Promise-based while the rest of @services is Observable-based, so every
 * call is unwrapped with firstValueFrom here rather than reshaping the store and every component
 * that depends on it.
 */
@Injectable({ providedIn: 'root' })
export class VoicemailApiService
  extends HttpService<never>
  implements VoicemailGateway
{
  protected override readonly collectionPath = '/voicemail';

  private get voicemailsUrl(): string {
    return `${this.apiRootUrl}/voicemails`;
  }

  reset(): void {
    // Nothing to drop: every call already goes to the API, so there is no per-provider state cached
    // here that could leak across a session switch.
    return;
  }

  async list(): Promise<Voicemail[]> {
    const items = await this.run(
      this.http.get<VoicemailDto[]>(this.voicemailsUrl),
      'Calls could not be loaded. Please try again.'
    );

    return items.map(toVoicemail);
  }

  async getTranscript(id: string): Promise<VmTranscript> {
    return toTranscript(
      await this.run(
        this.http.get<VoicemailTranscriptDto>(
          `${this.voicemailsUrl}/${id}/transcript`
        ),
        'The transcript could not be loaded. Please try again.'
      )
    );
  }

  async getSettings(): Promise<VmSettings> {
    // A read, and only a read. This used to reconcile whenever the line was "checking", which meant
    // every store load and every visit to the Phone tab could bind a number and activate a line --
    // and a reconcile failure took the whole inbox down with it, because the two are loaded
    // together. Reconciling is now what the Check status button does, and nothing else.
    return toVmSettings(
      await this.run(
        this.http.get<VoicemailSettingsDto>(`${this.apiUrl}/settings`),
        'Voicemail settings could not be loaded. Please try again.'
      )
    );
  }

  async reconcile(): Promise<VmSettings> {
    return toVmSettings(
      await this.run(
        this.http.post<VoicemailSettingsDto>(
          `${this.apiUrl}/line/reconcile`,
          {}
        ),
        'The activation status could not be checked. Please try again.'
      )
    );
  }

  async saveSettings(settings: VmSettings): Promise<VmSettings> {
    const updated = await this.run(
      this.http.put<VoicemailSettingsDto>(`${this.apiUrl}/settings`, {
        greeting: settings.greeting,
        // The override link is the only booking setting stored with the line. The destination
        // itself is derived from the Scheduling type and is never sent back.
        bookingLinkOverride: settings.overrideUrl?.trim() || null,
      }),
      'Your changes could not be saved. Please try again.'
    );

    return toVmSettings(updated);
  }

  async setWebsiteNumber(useVoicemailNumber: boolean): Promise<VmSettings> {
    return toVmSettings(
      await this.run(
        this.http.put<VoicemailSettingsDto>(
          `${this.apiUrl}/settings/website-number`,
          { useVoicemailNumber }
        ),
        'Your website number could not be changed. Please try again.'
      )
    );
  }

  async activate(): Promise<VmSettings> {
    const activated = await this.run(
      this.http.post<VoicemailSettingsDto>(`${this.apiUrl}/line`, {}),
      'Your voicemail number could not be set up. Please try again.'
    );

    return toVmSettings(activated);
  }

  async markUnread(id: string, unread: boolean): Promise<Voicemail> {
    const updated = await this.run(
      this.http.post<VoicemailDto>(`${this.voicemailsUrl}/${id}/read-state`, {
        unread,
      }),
      'This voicemail could not be updated. Please try again.'
    );

    return toVoicemail(updated);
  }

  async searchPatients(query: string): Promise<VmPatient[]> {
    if (query.trim().length < 2) return [];

    const matches = await this.run(
      this.http.get<VoicemailPatientDto[]>(
        `${this.voicemailsUrl}/patients/search`,
        { params: this.getHttpParameters({ query }) }
      ),
      'Patients could not be searched. Please try again.'
    );

    return matches.map(patient => ({
      id: patient.id,
      name: patient.name,
      phone: patient.phone ?? '',
      dob: patient.dateOfBirth ?? '',
    }));
  }

  async linkPatient(id: string, patientId: string | null): Promise<Voicemail> {
    const updated = await this.run(
      this.http.post<VoicemailDto>(`${this.voicemailsUrl}/${id}/link`, {
        patientId,
      }),
      'This voicemail could not be linked. Please try again.'
    );

    return toVoicemail(updated);
  }

  async getAudio(recordingRef: string): Promise<Blob> {
    return this.run(
      this.http.get(`${this.voicemailsUrl}/${recordingRef}/audio`, {
        responseType: 'blob',
      }),
      'The recording could not be loaded.'
    );
  }

  async getPreviewAudio(request: VmCallPreviewRequest): Promise<Blob> {
    const fallback = 'The call preview could not be played. Please try again.';
    try {
      return await firstValueFrom(
        this.http.post(`${this.apiUrl}/preview-audio`, request, {
          responseType: 'blob',
        })
      );
    } catch (error: unknown) {
      // The limiter answers in plain text with no code: every preview is billed speech, so playing
      // many in a row is held back per provider.
      if (error instanceof HttpErrorResponse && error.status === 429) {
        throw new VoicemailGatewayError(
          'You have played the preview many times in a row. Wait a minute, then try again.',
          null
        );
      }

      // Asked for a blob, the error body arrives as one too, so its code has to be read out of it.
      const code = await this.blobCodeOf(error);

      throw new VoicemailGatewayError(this.describe(code, fallback), code);
    }
  }

  /**
   * Unwrap an Observable and turn a failure into an Error whose message can be shown as-is.
   * The store renders `error.message` straight to the provider, so a raw HttpErrorResponse would put
   * a status line and a URL on screen.
   */
  private async run<T>(request: Observable<T>, fallback: string): Promise<T> {
    try {
      return await firstValueFrom(request);
    } catch (error: unknown) {
      const code = this.codeOf(error);

      throw new VoicemailGatewayError(this.describe(code, fallback), code);
    }
  }

  private codeOf(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) return null;

    const code: unknown = error.error?.code;

    return typeof code === 'string' ? code : null;
  }

  private async blobCodeOf(error: unknown): Promise<string | null> {
    if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob))
      return this.codeOf(error);

    try {
      const body: unknown = JSON.parse(await error.error.text());
      const code =
        typeof body === 'object' && body !== null && 'code' in body
          ? body.code
          : null;

      return typeof code === 'string' ? code : null;
    } catch {
      return null;
    }
  }

  private describe(code: string | null, fallback: string): string {
    const message = code ? VOICEMAIL_ERROR_COPY[code] : undefined;

    return message ?? fallback;
  }
}

/**
 * Error codes the API returns, mapped to what a provider can act on. Anything unmapped falls back to
 * the caller's own wording, so a new backend code degrades to a plain message rather than a blank.
 */
const VOICEMAIL_ERROR_COPY: Record<string, string> = {
  VOICEMAIL_UNAVAILABLE: 'Voicemail is not available on this account.',
  VOICEMAIL_LINE_NOT_FOUND: 'Set up your voicemail number first.',
  VOICEMAIL_DESTINATION_REQUIRED:
    'Publish your landing page or add a different link for texts before enabling voicemail.',
  VOICEMAIL_ACTIVATION_IN_PROGRESS:
    'Your voicemail line is already being set up.',
  VOICEMAIL_ACTIVATION_NEEDS_SUPPORT:
    'Your voicemail line needs help from support before it can be activated.',
  VOICEMAIL_NUMBER_UNAVAILABLE:
    'No voicemail number is available right now. Please try again later.',
  VOICEMAIL_TELEPHONY_UNAVAILABLE:
    'The phone provider could not complete the request. Please try again.',
  VOICEMAIL_NOT_FOUND: 'This voicemail is no longer available.',
  VOICEMAIL_HAS_NO_RECORDING: 'This voicemail has no recording yet.',
  VOICEMAIL_PREVIEW_UNAVAILABLE:
    'The call preview is not available right now. Please try again later.',
  VOICEMAIL_LINE_NOT_ACTIVE: 'Your voicemail line is not active.',
  VOICEMAIL_PRACTICE_PHONE_MISSING:
    'Add a practice phone number in Account settings before showing it on your website instead.',
};
